import { Role } from 'stores/AuthenticationStoreAdapter';

/**
 * 画面キー定義
 * 各画面を一意に識別するキー。ナビゲーション制御およびURL直打ち防止に使用。
 * NOTE: INDEX_HOME はログイン済みであれば常にアクセス可能なため権限制御対象外。
 */
export const ScreenKey = {
  // Management
  MGMT_TOP: 'MGMT_TOP',
  MGMT_USER: 'MGMT_USER',
  MGMT_NOTICE: 'MGMT_NOTICE',
  MGMT_INFO: 'MGMT_INFO',
  MGMT_IMPORTANT: 'MGMT_IMPORTANT',
  MGMT_FINANCIAL_YEAR: 'MGMT_FINANCIAL_YEAR',
  MGMT_SETTINGS: 'MGMT_SETTINGS',

  // Member
  MEMBER_TOP: 'MEMBER_TOP',
  MEMBER_PARTNER: 'MEMBER_PARTNER',
  MEMBER_CLIENT: 'MEMBER_CLIENT',
  MEMBER_PROJECT: 'MEMBER_PROJECT',
  MEMBER_MEMBER: 'MEMBER_MEMBER',

  // Contract
  CONTRACT_TOP: 'CONTRACT_TOP',
  CONTRACT_LIST: 'CONTRACT_LIST',
  CONTRACT_REQUEST_LIST: 'CONTRACT_REQUEST_LIST',
  CONTRACT_REQUEST_CREATE: 'CONTRACT_REQUEST_CREATE',
  CONTRACT_CHECKLIST: 'CONTRACT_CHECKLIST',
  CONTRACT_TEMPLATE: 'CONTRACT_TEMPLATE',
  CONTRACT_PASTE: 'CONTRACT_PASTE',

  // Assessment
  ASSESSMENT_TOP: 'ASSESSMENT_TOP',
  ASSESSMENT_MANAGEMENT: 'ASSESSMENT_MANAGEMENT',

  // Security
  SECURITY_TOP: 'SECURITY_TOP',
  SECURITY_CHECK_MANAGEMENT: 'SECURITY_CHECK_MANAGEMENT',
  SECURITY_CHECK: 'SECURITY_CHECK',

  // Training
  // TOP / マイワークスペースは権限なし（全認証済みユーザーがアクセス可能）
  TRAINING_TOP: 'TRAINING_TOP',
  TRAINING_WORKSPACE: 'TRAINING_WORKSPACE',
  // 他人のワークスペース閲覧は DEVELOPER / ADMIN のみ
  TRAINING_ADMIN_WORKSPACE: 'TRAINING_ADMIN_WORKSPACE',

  // Training 研修管理（DEVELOPER のみ）
  TRAINING_ADMIN_TOP: 'TRAINING_ADMIN_TOP',
  TRAINING_ADMIN_ASSIGNMENT: 'TRAINING_ADMIN_ASSIGNMENT',
  TRAINING_ADMIN_QUESTION: 'TRAINING_ADMIN_QUESTION',
  TRAINING_ADMIN_PROGRESS: 'TRAINING_ADMIN_PROGRESS',

  // Training 研修受講（権限制御対象外 — 全認証済みユーザーがアクセス可能）
  TRAINING_LEARN_TOP: 'TRAINING_LEARN_TOP',
  TRAINING_LEARN_ASSIGNMENT: 'TRAINING_LEARN_ASSIGNMENT',

  // Guide (権限制御対象外 — 全認証済みユーザーがアクセス可能)
  GUIDE_TOP: 'GUIDE_TOP',

  // Guide (DEVELOPER限定)
  GUIDE_DEPLOY: 'GUIDE_DEPLOY',

  // DevDocs (権限制御対象外 — 全認証済みユーザーがアクセス可能)
  DEVDOCS_TOP: 'DEVDOCS_TOP',

  // Ticket (全認証済みユーザーがアクセス可能。全ロールに付与)
  TICKET_TOP: 'TICKET_TOP',
  TICKET_LIST: 'TICKET_LIST',
  TICKET_BOARD: 'TICKET_BOARD',
  TICKET_GANTT: 'TICKET_GANTT',
  TICKET_TYPE: 'TICKET_TYPE',

  // Expense（個人の家計簿。全認証済みユーザーがアクセス可能。全ロールに付与）
  EXPENSE_TOP: 'EXPENSE_TOP',
  EXPENSE_DASHBOARD: 'EXPENSE_DASHBOARD',
  EXPENSE_MANAGEMENT: 'EXPENSE_MANAGEMENT',
  EXPENSE_LABEL: 'EXPENSE_LABEL',
  EXPENSE_STORE: 'EXPENSE_STORE',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ScreenKey = (typeof ScreenKey)[keyof typeof ScreenKey];

/**
 * パス末尾 → ScreenKey のマッピング
 * URL直打ち防止ガード (ScreenGuard) で使用する。
 */
export const PATH_SCREEN_MAP: Record<string, ScreenKey> = {
  // Management
  management: ScreenKey.MGMT_TOP,
  user: ScreenKey.MGMT_USER,
  notice: ScreenKey.MGMT_NOTICE,
  info: ScreenKey.MGMT_INFO,
  important: ScreenKey.MGMT_IMPORTANT,
  'financial-year': ScreenKey.MGMT_FINANCIAL_YEAR,
  settings: ScreenKey.MGMT_SETTINGS,

  // Member
  member: ScreenKey.MEMBER_TOP,
  partner: ScreenKey.MEMBER_PARTNER,
  client: ScreenKey.MEMBER_CLIENT,
  project: ScreenKey.MEMBER_PROJECT,
  'member-mgmt': ScreenKey.MEMBER_MEMBER,

  // Contract
  contract: ScreenKey.CONTRACT_TOP,
  'contract-list': ScreenKey.CONTRACT_LIST,
  request: ScreenKey.CONTRACT_REQUEST_LIST,
  'contract-request-create': ScreenKey.CONTRACT_REQUEST_CREATE,
  checklist: ScreenKey.CONTRACT_CHECKLIST,
  template: ScreenKey.CONTRACT_TEMPLATE,
  paste: ScreenKey.CONTRACT_PASTE,

  // Assessment
  assessment: ScreenKey.ASSESSMENT_TOP,
  'assessment-management': ScreenKey.ASSESSMENT_MANAGEMENT,

  // Security
  security: ScreenKey.SECURITY_TOP,
  'security-check-management': ScreenKey.SECURITY_CHECK_MANAGEMENT,
  'security-check': ScreenKey.SECURITY_CHECK,

  // Workspace（研修ワークスペース。ドメイン index = /workspace）
  workspace: ScreenKey.TRAINING_WORKSPACE,

  // Training 研修管理（DEVELOPER のみ）
  'training-admin': ScreenKey.TRAINING_ADMIN_TOP,
  'training-assignment': ScreenKey.TRAINING_ADMIN_ASSIGNMENT,
  'training-question': ScreenKey.TRAINING_ADMIN_QUESTION,
  'training-progress': ScreenKey.TRAINING_ADMIN_PROGRESS,

  // Training 研修受講（権限制御対象外）
  'training-learn': ScreenKey.TRAINING_LEARN_TOP,
  'learn-assignment': ScreenKey.TRAINING_LEARN_ASSIGNMENT,
  'all-questions': ScreenKey.TRAINING_LEARN_ASSIGNMENT,

  // Ticket（サイドバー非表示ドメイン。全ロールに付与）
  ticket: ScreenKey.TICKET_TOP,
  'ticket-list': ScreenKey.TICKET_LIST,
  board: ScreenKey.TICKET_BOARD,
  gantt: ScreenKey.TICKET_GANTT,
  // 種別設定はグローバル/プライベートの2画面（末尾セグメントで判定）
  global: ScreenKey.TICKET_TYPE,
  private: ScreenKey.TICKET_TYPE,

  // Expense（経費管理）
  expense: ScreenKey.EXPENSE_TOP,
  'expense-dashboard': ScreenKey.EXPENSE_DASHBOARD,
  'expense-management': ScreenKey.EXPENSE_MANAGEMENT,
  'expense-label': ScreenKey.EXPENSE_LABEL,
  'expense-store': ScreenKey.EXPENSE_STORE,
};

/** 全ロールに付与するチケット画面キー（チケットは誰でも利用可能） */
const TICKET_SCREENS: ScreenKey[] = [
  ScreenKey.TICKET_TOP,
  ScreenKey.TICKET_LIST,
  ScreenKey.TICKET_BOARD,
  ScreenKey.TICKET_GANTT,
  ScreenKey.TICKET_TYPE,
];

/**
 * 全ロールに付与する経費管理画面キー。
 * 経費は個人の家計簿であり画面単位の権限制限は付けない（認証のみ）。
 * 個人データは BE の user_id 隔離で保護する。
 */
const EXPENSE_SCREENS: ScreenKey[] = [
  ScreenKey.EXPENSE_TOP,
  ScreenKey.EXPENSE_DASHBOARD,
  ScreenKey.EXPENSE_MANAGEMENT,
  ScreenKey.EXPENSE_LABEL,
  ScreenKey.EXPENSE_STORE,
];

/**
 * ロールごとにアクセスを許可する画面キーのマッピング
 * 将来的にはBackend側のテーブルで管理し、userSession APIから取得する形に移行予定。
 */
export const ROLE_SCREEN_MAP: Record<Role, ScreenKey[]> = {
  ADMIN: [
    ScreenKey.MGMT_TOP,
    ScreenKey.MGMT_USER,
    ScreenKey.MGMT_NOTICE,
    ScreenKey.MGMT_INFO,
    ScreenKey.MGMT_IMPORTANT,
    ScreenKey.MGMT_FINANCIAL_YEAR,
    ScreenKey.MGMT_SETTINGS,
    ScreenKey.MEMBER_TOP,
    ScreenKey.MEMBER_PARTNER,
    ScreenKey.MEMBER_CLIENT,
    ScreenKey.MEMBER_PROJECT,
    ScreenKey.MEMBER_MEMBER,
    ScreenKey.CONTRACT_TOP,
    ScreenKey.CONTRACT_LIST,
    ScreenKey.CONTRACT_REQUEST_LIST,
    ScreenKey.CONTRACT_REQUEST_CREATE,
    ScreenKey.CONTRACT_CHECKLIST,
    ScreenKey.CONTRACT_TEMPLATE,
    ScreenKey.CONTRACT_PASTE,
    ScreenKey.ASSESSMENT_TOP,
    ScreenKey.ASSESSMENT_MANAGEMENT,
    ScreenKey.SECURITY_TOP,
    ScreenKey.SECURITY_CHECK_MANAGEMENT,
    ScreenKey.SECURITY_CHECK,
    ScreenKey.TRAINING_TOP,
    ScreenKey.TRAINING_WORKSPACE,
    ScreenKey.TRAINING_ADMIN_WORKSPACE,
    ScreenKey.TRAINING_LEARN_TOP,
    ScreenKey.TRAINING_LEARN_ASSIGNMENT,
    ...TICKET_SCREENS,
    ...EXPENSE_SCREENS,
  ],
  MANAGER: [
    ScreenKey.MGMT_TOP,
    ScreenKey.MGMT_NOTICE,
    ScreenKey.MEMBER_TOP,
    ScreenKey.MEMBER_PARTNER,
    ScreenKey.MEMBER_CLIENT,
    ScreenKey.MEMBER_PROJECT,
    ScreenKey.MEMBER_MEMBER,
    ScreenKey.CONTRACT_TOP,
    ScreenKey.CONTRACT_LIST,
    ScreenKey.CONTRACT_REQUEST_LIST,
    ScreenKey.CONTRACT_REQUEST_CREATE,
    ScreenKey.ASSESSMENT_TOP,
    ScreenKey.ASSESSMENT_MANAGEMENT,
    ScreenKey.SECURITY_TOP,
    ScreenKey.SECURITY_CHECK_MANAGEMENT,
    ScreenKey.SECURITY_CHECK,
    ScreenKey.TRAINING_TOP,
    ScreenKey.TRAINING_WORKSPACE,
    ScreenKey.TRAINING_LEARN_TOP,
    ScreenKey.TRAINING_LEARN_ASSIGNMENT,
    ...TICKET_SCREENS,
    ...EXPENSE_SCREENS,
  ],
  BACK_OFFICE: [
    ScreenKey.MGMT_TOP,
    ScreenKey.MGMT_NOTICE,
    ScreenKey.MGMT_INFO,
    ScreenKey.MGMT_IMPORTANT,
    ScreenKey.MEMBER_TOP,
    ScreenKey.MEMBER_PARTNER,
    ScreenKey.MEMBER_CLIENT,
    ScreenKey.MEMBER_PROJECT,
    ScreenKey.MEMBER_MEMBER,
    ScreenKey.CONTRACT_TOP,
    ScreenKey.CONTRACT_LIST,
    ScreenKey.CONTRACT_REQUEST_LIST,
    ScreenKey.CONTRACT_REQUEST_CREATE,
    ScreenKey.CONTRACT_CHECKLIST,
    ScreenKey.CONTRACT_TEMPLATE,
    ScreenKey.CONTRACT_PASTE,
    ScreenKey.ASSESSMENT_TOP,
    ScreenKey.ASSESSMENT_MANAGEMENT,
    ScreenKey.SECURITY_TOP,
    ScreenKey.SECURITY_CHECK_MANAGEMENT,
    ScreenKey.SECURITY_CHECK,
    ScreenKey.TRAINING_TOP,
    ScreenKey.TRAINING_WORKSPACE,
    ScreenKey.TRAINING_LEARN_TOP,
    ScreenKey.TRAINING_LEARN_ASSIGNMENT,
    ...TICKET_SCREENS,
    ...EXPENSE_SCREENS,
  ],
  GENERAL: [
    ScreenKey.SECURITY_TOP,
    ScreenKey.SECURITY_CHECK,
    ScreenKey.TRAINING_TOP,
    ScreenKey.TRAINING_WORKSPACE,
    ScreenKey.TRAINING_LEARN_TOP,
    ScreenKey.TRAINING_LEARN_ASSIGNMENT,
    ...TICKET_SCREENS,
    ...EXPENSE_SCREENS,
  ],
  DEVELOPER: [
    ScreenKey.MGMT_TOP,
    ScreenKey.MGMT_USER,
    ScreenKey.MGMT_NOTICE,
    ScreenKey.MGMT_INFO,
    ScreenKey.MGMT_IMPORTANT,
    ScreenKey.MGMT_FINANCIAL_YEAR,
    ScreenKey.MGMT_SETTINGS,
    ScreenKey.MEMBER_TOP,
    ScreenKey.MEMBER_PARTNER,
    ScreenKey.MEMBER_CLIENT,
    ScreenKey.MEMBER_PROJECT,
    ScreenKey.MEMBER_MEMBER,
    ScreenKey.CONTRACT_TOP,
    ScreenKey.CONTRACT_LIST,
    ScreenKey.CONTRACT_REQUEST_LIST,
    ScreenKey.CONTRACT_REQUEST_CREATE,
    ScreenKey.CONTRACT_CHECKLIST,
    ScreenKey.CONTRACT_TEMPLATE,
    ScreenKey.CONTRACT_PASTE,
    ScreenKey.ASSESSMENT_TOP,
    ScreenKey.ASSESSMENT_MANAGEMENT,
    ScreenKey.SECURITY_TOP,
    ScreenKey.SECURITY_CHECK_MANAGEMENT,
    ScreenKey.SECURITY_CHECK,
    ScreenKey.TRAINING_TOP,
    ScreenKey.TRAINING_WORKSPACE,
    ScreenKey.TRAINING_ADMIN_WORKSPACE,
    ScreenKey.TRAINING_LEARN_TOP,
    ScreenKey.TRAINING_LEARN_ASSIGNMENT,
    // 研修管理は DEVELOPER のみ（最小権限の原則・D7-A）
    ScreenKey.TRAINING_ADMIN_TOP,
    ScreenKey.TRAINING_ADMIN_ASSIGNMENT,
    ScreenKey.TRAINING_ADMIN_QUESTION,
    ScreenKey.TRAINING_ADMIN_PROGRESS,
    ScreenKey.GUIDE_DEPLOY,
    ...TICKET_SCREENS,
    ...EXPENSE_SCREENS,
  ],
};

/**
 * ユーザーのロール一覧から、利用可能な画面キーの一覧を導出する。
 */
export const resolveAllowedScreens = (roles: Role[]): ScreenKey[] => {
  const screenSet = new Set<ScreenKey>();
  roles.forEach((role) => {
    const screens = ROLE_SCREEN_MAP[role];
    if (screens) {
      screens.forEach((s) => screenSet.add(s));
    }
  });
  return Array.from(screenSet);
};
